/**
 * Redaction and normalization helpers for Sentry API error reporting.
 * Enriched reporting sends request/response bodies, so secrets and personal data
 * must be stripped before anything leaves the browser.
 */

/** Keys whose values must never reach Sentry — credentials and personal data. */
const SENSITIVE_KEY_PATTERN =
	/token|password|secret|authorization|cookie|api[-_]?key|access[-_]?token|refresh[-_]?token|otp|credential|email|phone|mobile|address|dob|date[-_]?of[-_]?birth|ssn|resume|first[-_]?name|last[-_]?name|full[-_]?name|^name$/i;

/**
 * Value shapes that are secrets regardless of the key they sit under: JWTs, and
 * long unbroken base64/hex runs. Key-name matching alone misses a token posted as
 * a bare string body or under an unrecognised key.
 */
const JWT_PATTERN = /^ey[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]+$/;
const LONG_OPAQUE_PATTERN = /^[A-Za-z0-9_\-+/=]{40,}$/;

const REDACTED = '[redacted]';
const MAX_DEPTH_MARKER = '[max-depth]';
const MAX_SERIALIZED_LENGTH = 1000;

const truncate = (value: string): string =>
	value.length > MAX_SERIALIZED_LENGTH
		? `${value.slice(0, MAX_SERIALIZED_LENGTH)}…[truncated]`
		: value;

/** Redact a string that looks like a credential even when its key looked innocuous. */
const redactSecretString = (value: string): string =>
	JWT_PATTERN.test(value) || LONG_OPAQUE_PATTERN.test(value) ? REDACTED : value;

/**
 * Recursively replace sensitive values.
 *
 * Past the depth cap we emit a marker rather than the raw sub-tree — returning the
 * value there would hand back exactly the secrets this function exists to strip.
 * `seen` breaks cycles, which the depth cap alone does not.
 */
function redactValue(value: unknown, depth = 0, seen = new WeakSet<object>()): unknown {
	if (value === null || value === undefined) return value;
	if (typeof value === 'string') return redactSecretString(value);
	if (typeof value !== 'object') return value;

	if (depth > 6) return MAX_DEPTH_MARKER;
	if (seen.has(value as object)) return '[circular]';
	seen.add(value as object);

	if (Array.isArray(value)) return value.map((item) => redactValue(item, depth + 1, seen));

	// FormData/Blob/File and friends serialize to `{}`, which reads as "empty body"
	// rather than "not serializable" — describe them instead.
	const describedBinary = describeNonPlainObject(value);
	if (describedBinary) return describedBinary;

	const out: Record<string, unknown> = {};
	for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
		out[key] = SENSITIVE_KEY_PATTERN.test(key) ? REDACTED : redactValue(val, depth + 1, seen);
	}
	return out;
}

/** Describe browser payload types that JSON.stringify flattens to `{}`. */
function describeNonPlainObject(value: object): string | undefined {
	if (typeof FormData !== 'undefined' && value instanceof FormData) {
		const keys = Array.from(value.keys());
		return `[FormData: ${keys.length} field(s) — ${keys.join(', ') || 'none'}]`;
	}
	if (typeof Blob !== 'undefined' && value instanceof Blob) {
		return `[Blob: ${value.size} bytes, ${value.type || 'unknown type'}]`;
	}
	if (typeof ArrayBuffer !== 'undefined' && value instanceof ArrayBuffer) {
		return `[ArrayBuffer: ${value.byteLength} bytes]`;
	}
	if (typeof URLSearchParams !== 'undefined' && value instanceof URLSearchParams) {
		return `[URLSearchParams: ${Array.from(value.keys()).join(', ')}]`;
	}
	return undefined;
}

/**
 * Serialize a request/response body for Sentry: parse if it is a JSON string,
 * redact sensitive keys and secret-shaped values, then truncate.
 */
export function redactAndTruncate(body: unknown): string | undefined {
	if (body === null || body === undefined || body === '') return undefined;
	try {
		if (typeof body === 'string') {
			// Truncate before parsing so a multi-MB non-JSON body is never fully scanned.
			if (body.length > MAX_SERIALIZED_LENGTH) return truncate(redactSecretString(body));
			try {
				return truncate(JSON.stringify(redactValue(JSON.parse(body))));
			} catch {
				// Not JSON — a bare token must still be redacted, not passed through.
				return truncate(redactSecretString(body));
			}
		}
		if (typeof body === 'object') {
			const redacted = redactValue(body);
			return truncate(typeof redacted === 'string' ? redacted : JSON.stringify(redacted));
		}
		return truncate(String(body));
	} catch {
		return '[unserializable]';
	}
}

/**
 * Collapse ids out of a URL path so the same endpoint groups into one Sentry issue.
 * Patterns are anchored to whole path segments — an unanchored match would eat the
 * first 24 chars of a longer hex id and leave a per-request tail, which fragments
 * grouping instead of collapsing it. Most specific pattern runs first.
 */
export function normalizeEndpoint(url: string): string {
	const withoutQuery = url.split('?')[0];
	return withoutQuery
		.replace(
			/(?<=^|\/)[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?=\/|$)/gi,
			':uuid'
		)
		.replace(/(?<=^|\/)[0-9a-f]{24}(?=\/|$)/gi, ':id')
		.replace(/(?<=^|\/)\d+(?=\/|$)/g, ':num');
}
